// ─── LivePulse ────────────────────────────────────────────────────────────────
// A pulsing dot indicator used for live/active state signals.

interface LivePulseProps {
  /** Color variant of the dot */
  variant?: 'blood' | 'gold' | 'steel';
  /** Size of the dot in Tailwind size classes */
  size?: 'sm' | 'md';
  className?: string;
}

const VARIANT_MAP: Record<NonNullable<LivePulseProps['variant']>, string> = {
  blood: 'bg-primary shadow-[0_0_8px_rgba(205,1,1,0.9)]',
  gold: 'bg-secondary shadow-[0_0_6px_rgba(241,198,41,0.8)]',
  steel: 'bg-iron-300 shadow-[0_0_5px_rgba(172,177,178,0.6)]',
};

const SIZE_MAP: Record<NonNullable<LivePulseProps['size']>, string> = {
  sm: 'w-1.5 h-1.5',
  md: 'w-2 h-2',
};

export function LivePulse({
  variant = 'blood',
  size = 'sm',
  className = '',
}: LivePulseProps) {
  return (
    <span
      className={`rounded-full animate-pulse inline-block shrink-0 ${VARIANT_MAP[variant]} ${SIZE_MAP[size]} ${className}`}
    />
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────────
// Horizontal rule label pattern: "Title ─────── trailing"

interface SectionHeaderProps {
  title: string;
  trailing?: React.ReactNode;
  /** HTML element level (cosmetic only — all render as the same styled div) */
  as?: 'h2' | 'h3' | 'p';
  className?: string;
}

export function SectionHeader({
  title,
  trailing,
  as: Tag = 'h2',
  className = '',
}: SectionHeaderProps) {
  return (
    <div className={`flex items-center gap-6 ${className}`}>
      <Tag className="text-xl text-iron-300 uppercase tracking-widest font-epic whitespace-nowrap">
        {title}
      </Tag>
      <div className="h-px grow bg-iron-800" />
      {trailing && (
        <span className="text-[9px] tracking-widest text-iron-600 uppercase font-bold whitespace-nowrap">
          {trailing}
        </span>
      )}
    </div>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
// Single key-value stat tile used in the bottom stats row.

interface StatCardProps {
  label: string;
  value: string;
  /** When true, value renders in Baratheon Gold */
  accent?: boolean;
}

export function StatCard({ label, value, accent = false }: StatCardProps) {
  return (
    <div className="bg-iron-900 border border-iron-800 px-4 py-4">
      <p className="text-[9px] tracking-[0.25em] uppercase text-iron-500 font-bold mb-2">
        {label}
      </p>
      <p className={`font-epic text-sm font-bold ${accent ? 'text-secondary' : 'text-iron-200'}`}>
        {value}
      </p>
    </div>
  );
}

// ─── IronDivider ──────────────────────────────────────────────────────────────
// Simple 1px horizontal rule in the iron-800 palette.

export function IronDivider({ className = '' }: { className?: string }) {
  return <div className={`h-px bg-iron-800 ${className}`} />;
}

// ─── FieldLabel ───────────────────────────────────────────────────────────────
// All-caps micro-label used above values throughout the design system.

export function FieldLabel({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p className={`text-[9px] tracking-[0.3em] uppercase text-iron-500 font-bold ${className}`}>
      {children}
    </p>
  );
}
